<script>
  import { mapState, mapActions } from 'vuex';
  export default {
    name: "Finishing",
    data() {
      return {
        showSuccess: false
      };
    },
    computed: {
      ...mapState('knowledge', [
        'uploadedFile', 
        'datasetId', 
        'crawlResult', 
        'knowledgeBaseName', 
        'uploadedFiles'
      ]),
      displayName() {
        // Use the explicitly set name first, fallback to other options if not available
        if (this.knowledgeBaseName) {
          return this.knowledgeBaseName;
        } else if (this.uploadedFile) {
          return this.uploadedFile.name;
        } else if (this.uploadedFiles && this.uploadedFiles.length > 0) {
          return this.uploadedFiles[0].name;
        } else if (this.crawlResult && this.crawlResult.data && this.crawlResult.data.length > 0) {
          return this.crawlResult.data[0].metadata.title;
        }
        return this.$t('KNOWLEDGE.FINISHING.DEFAULT_NAME');
      },
      documentCount() {
        if (this.uploadedFiles && this.uploadedFiles.length > 0) {
          return this.uploadedFiles.length;
        } else if (this.uploadedFile) {
          return 1;
        } else if (this.crawlResult && this.crawlResult.data) {
          return this.crawlResult.data.length;
        }
        return 0;
      }
    },
    methods: {
      ...mapActions('knowledge', ['handleRemoveFile'])
    },
    mounted() {
      // Trigger success animation after a short delay
      setTimeout(() => {
        this.showSuccess = true;
      }, 300);
    },
    unmounted() {
      this.handleRemoveFile();
    }
  }
</script>

<template>
  <div class="p-2 md:p-8 flex flex-1 h-full gap-3 flex-col m-0 overflow-auto">
    <!-- Success banner -->
    <div 
      v-show="showSuccess"
      class="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg shadow-sm transition-all duration-500 ease-in-out transform -translate-y-0 opacity-100"
    >
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <!-- Success checkmark with animation -->
          <div class="w-10 h-10 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
            <svg 
              class="w-6 h-6 text-green-600 dark:text-green-300" 
              :class="{'animate-bounce': showSuccess}"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
        </div>
        <div class="ml-3">
          <h3 class="text-lg font-medium text-green-800 dark:text-green-200">{{ $t('KNOWLEDGE.FINISHING.SUCCESS_TITLE') }}</h3>
          <div class="mt-1 text-sm text-green-700 dark:text-green-300">
            {{ $t('KNOWLEDGE.FINISHING.SUCCESS_MESSAGE') }}
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
      <header>
        <h1 class="flex items-center mb-1 text-2xl font-medium text-slate-900 dark:text-slate-100">
          {{ $t('KNOWLEDGE.FINISHING.HEADER_TITLE') }}
        </h1>
        <p class="text-slate-600 dark:text-slate-400">
          {{ $t('KNOWLEDGE.FINISHING.HEADER_DESCRIPTION') }}
        </p>
      </header>

      <!-- Added flex-wrap so buttons don't overflow on small screens -->
      <div class="flex gap-4 items-center mt-4 md:mt-0 flex-wrap">
        <router-link 
          :to="{ name: 'knowledge'}" 
          class="inline-flex items-center justify-center gap-2 bg-woot-500 text-slate-100 px-5 py-2.5 rounded-md hover:bg-woot-700 transition-colors shadow-sm flex-shrink-0"
        >
          <fluent-icon icon="arrow-redo" />
          {{ $t('KNOWLEDGE.FINISHING.BACK_TO_LIST') }}
        </router-link>

        <router-link
          :to="`../view/${datasetId}/document`"
          class="inline-flex items-center justify-center gap-2 bg-woot-500 text-slate-100 px-5 py-2.5 rounded-md hover:bg-woot-700 transition-colors shadow-sm flex-shrink-0"
          data-testid="finishing_view_knowledge_base_button"
        >
          <fluent-icon icon="book" />
          {{ $t('KNOWLEDGE.FINISHING.ADD_MORE_DOCUMENTS') }}
        </router-link>
      </div>
    </div>

    <div class="bg-white dark:bg-slate-800 shadow-sm rounded-lg overflow-hidden mb-6">
      <div class="p-5 border-b border-slate-200 dark:border-slate-700">        
        <h2 class="text-lg font-medium text-slate-900 dark:text-slate-100">{{ $t('KNOWLEDGE.FINISHING.DETAILS_SECTION_TITLE') }}</h2>
      </div>
      <div class="p-5">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Knowledge Base Name -->
          <div>
            <h3 class="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">{{ $t('KNOWLEDGE.FINISHING.NAME_LABEL') }}</h3>
            <div class="bg-slate-100 dark:bg-slate-700 rounded-md p-3 font-medium text-slate-900 dark:text-slate-100" data-testid="finishing_knowledge_name">
              {{ displayName }}
            </div>
          </div>
          
          <!-- Document Count (added space between number and label) -->
          <div>
            <h3 class="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">{{ $t('KNOWLEDGE.FINISHING.DOCUMENTS_LABEL') }}</h3>
            <div class="bg-slate-100 dark:bg-slate-700 rounded-md p-3 font-medium text-slate-900 dark:text-slate-100">
              {{ documentCount }} {{ documentCount === 1 ? 'document' : 'documents' }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Next Steps -->
    <div class="bg-white dark:bg-slate-800 shadow-sm rounded-lg overflow-hidden">
      <div class="p-5 border-b border-slate-200 dark:border-slate-700">
        <h2 class="text-lg font-medium text-slate-900 dark:text-slate-100">{{ $t('KNOWLEDGE.FINISHING.NEXT_STEPS_TITLE') }}</h2>
      </div>
      <div class="p-5">
        <div class="space-y-4">
          <div class="flex items-start">
            <div class="flex-shrink-0 h-5 w-5 text-green-500">
              <svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-base text-slate-700 dark:text-slate-300" v-html="$t('KNOWLEDGE.FINISHING.SUGGESTION_1')"></p>
            </div>
          </div>
          <div class="flex items-start">
            <div class="flex-shrink-0 h-5 w-5 text-green-500">
              <svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-base text-slate-700 dark:text-slate-300" v-html="$t('KNOWLEDGE.FINISHING.SUGGESTION_2')"></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}
</style>